import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { AuditLogApi } from "../../../api/common/apiIndex";
import clsx from "clsx";

function SuccessBadge({ ok }) {
  return (
    <span
      className={clsx(
        "badge rounded-pill text-uppercase fw-semibold",
        ok
          ? "bg-success-subtle text-success-emphasis border border-success-subtle"
          : "bg-danger-subtle text-danger-emphasis border border-danger-subtle"
      )}
    >
      {ok ? "Success" : "Failure"}
    </span>
  );
}

function Row({ label, children }) {
  return (
    <div className="col-md-4">
      <div className="small text-uppercase text-muted fw-semibold">{label}</div>
      <div>{children}</div>
    </div>
  );
}

export default function AuditLogDetails() {
  const { auditId } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["audit", "detail", auditId],
    queryFn: () => AuditLogApi.getById(auditId),
    enabled: !!auditId,
  });

  return (
    <div className="container-fluid py-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="fs-5 mb-1">Audit Log Details</h4>
          <p className="text-muted mb-0">Log ID: <code>{auditId}</code></p>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          {isLoading && <div className="text-muted">Loading…</div>}
          {isError && <div className="text-danger">Failed to load details.</div>}

          {data && (
            <>
              <div className="row g-3 mb-3">
                <Row label="Occurred At">{new Date(data.occurredAt).toLocaleString()}</Row>
                <Row label="Action">{data.action}</Row>
                <Row label="Status"><SuccessBadge ok={data.success} /></Row>

                <Row label="Entity">
                  <>
                    {data.entityType}
                    {data.entityId ? ` #${data.entityId}` : ""}
                  </>
                </Row>
                <Row label="Error Code">{data.errorCode || "—"}</Row>
                <Row label="Business">{data.businessId ?? "—"}</Row>

                <Row label="Actor Email">{data.actorEmail || "—"}</Row>
                <Row label="Actor User ID">{data.actorUserId ?? "—"}</Row>
                <Row label="Actor Roles">{data.actorRoles || "—"}</Row>

                <Row label="Path">
                  <span className="text-truncate d-block" title={data.path}>{data.path}</span>
                </Row>
                <Row label="IP">{data.ip || "—"}</Row>
                <Row label="User Agent">
                  <span className="text-truncate d-block" title={data.userAgent}>
                    {data.userAgent || "—"}
                  </span>
                </Row>

                <div className="col-12">
                  <div className="small text-uppercase text-muted fw-semibold mb-1">Message</div>
                  <div className="border rounded p-2 bg-body-tertiary">{data.message || "—"}</div>
                </div>
              </div>

              {data.changes?.length ? (
                <div className="mt-3">
                  <h6 className="fw-semibold mb-2">Field Changes</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead className="table-light text-uppercase small text-muted">
                        <tr>
                          <th style={{ width: "20%" }}>Field</th>
                          <th>Before</th>
                          <th>After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.changes.map((c, idx) => (
                          <tr key={idx}>
                            <td><code>{c.field}</code></td>
                            <td><pre className="mb-0 text-wrap">{c.beforeV ?? "—"}</pre></td>
                            <td><pre className="mb-0 text-wrap">{c.afterV ?? "—"}</pre></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <div className="row g-3 mt-2">
                <div className="col-md-6">
                  <h6 className="fw-semibold">Before JSON</h6>
                  <pre className="border rounded p-3 bg-body-tertiary" style={{ maxHeight: 300, overflow: "auto" }}>
                    {data.beforeJson ? JSON.stringify(data.beforeJson, null, 2) : "—"}
                  </pre>
                </div>
                <div className="col-md-6">
                  <h6 className="fw-semibold">After JSON</h6>
                  <pre className="border rounded p-3 bg-body-tertiary" style={{ maxHeight: 300, overflow: "auto" }}>
                    {data.afterJson ? JSON.stringify(data.afterJson, null, 2) : "—"}
                  </pre>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
